/**
 * Spec 053, step 2: «Опыт не разобран» (`/knowledge/experience`) — tasks closed without looking
 * their experience through leave a note here. A person decides each one as a head of a unit
 * decides its unit's: into a new method, into a new version of a method close to it, or turned
 * down with a reason (a repeat, a special case, a mistake). No client's name goes into a method:
 * the server refuses it with the words found.
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Experience, type ExperienceNote } from "@/api/experience";
import { Methods } from "@/api/resources";
import { Button } from "@/components/ui/Button";
import { TextArea, TextField } from "@/components/ui/Field";
import { SkeletonList } from "@/components/ui/Skeleton";
import { useToast } from "@/components/ui/Toast";
import { ago, AppBar, Chip, EmptyNote, ScreenHeader } from "@/kit";
import { Markdown } from "@/lib/markdown";

type Mode = "method" | "version" | "reject";

export function ExperienceScreen() {
  const notes = useQuery({ queryKey: ["experience-notes"], queryFn: Experience.list });
  return (
    <div className="flex flex-col gap-3">
      <AppBar fallback="/knowledge" context="Знания" />
      <ScreenHeader title="Опыт не разобран" sub="Задачи закрыли, не разобрав опыт: реши, что из него сохранить." />
      {notes.isLoading && <SkeletonList count={3} />}
      {notes.isError && <EmptyNote>Не удалось загрузить заметки. Попробуй ещё раз.</EmptyNote>}
      {notes.data?.length === 0 && <EmptyNote>Всё разобрано: заметок нет.</EmptyNote>}
      {(notes.data ?? []).map((note) => (
        <NoteCard key={note.id} note={note} />
      ))}
    </div>
  );
}

function NoteCard({ note }: { note: ExperienceNote }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [mode, setMode] = useState<Mode | null>(null);
  const [title, setTitle] = useState("");
  const [summary, setSummary] = useState("");
  const [body, setBody] = useState(note.body_md);
  const [methodId, setMethodId] = useState<string | null>(note.similar[0]?.id ?? null);
  const [reason, setReason] = useState("");
  // A new version is the whole method: it starts from the current version's steps, with the note
  // under them to fold in — never from the note alone, which would wipe the method.
  const method = useQuery({
    queryKey: ["methods", "detail", methodId],
    queryFn: () => Methods.get(methodId!),
    enabled: mode === "version" && !!methodId,
  });
  const [prefilled, setPrefilled] = useState<string | null>(null);
  if (mode === "version" && method.data && prefilled !== method.data.id) {
    const current = method.data.versions.find((v) => v.id === method.data!.current_version_id);
    setPrefilled(method.data.id);
    setBody(`${current?.body_md ?? ""}\n\n${note.body_md}`.trim());
  }
  const done = (text: string) => {
    toast.success(text);
    void qc.invalidateQueries({ queryKey: ["experience-notes"] });
    void qc.invalidateQueries({ queryKey: ["methods"] });
  };
  const save = useMutation({
    mutationFn: () =>
      mode === "version"
        ? Experience.toMethod({ note_ids: [note.id], method_id: methodId, body_md: body.trim(), change_note: summary.trim() || null })
        : Experience.toMethod({ note_ids: [note.id], title: title.trim(), summary: summary.trim() || null, body_md: body.trim() }),
    onSuccess: (made) => done(made.new_method ? `Метод «${made.method_title}» записан` : `«${made.method_title}»: версия ${made.version_number}`),
    onError: (e: Error) => toast.error(e.message),
  });
  const refuse = useMutation({
    mutationFn: () => Experience.reject(note.id, reason.trim()),
    onSuccess: () => done("Заметка отклонена"),
    onError: (e: Error) => toast.error(e.message),
  });
  const ready = mode === "reject" ? !!reason.trim() : !!body.trim() && (mode === "version" ? !!methodId : !!title.trim());

  return (
    <section className="flex flex-col gap-2 rounded-card border border-p-border bg-p-surface px-4 py-3.5">
      <Markdown className="text-body text-p-text" text={note.body_md} />
      <p className="text-meta text-p-text-muted">
        {note.author} · {ago(note.created_at)}
        {note.task_id && (
          <>
            {" · "}
            <Link to={`/tasks/${note.task_id}`} className="font-semibold text-p-link">задача</Link>
          </>
        )}
      </p>
      {note.similar.length > 0 && (
        <p className="text-meta text-p-text-muted">
          Похожие методы:{" "}
          {note.similar.map((m, i) => (
            <span key={m.id}>
              {i > 0 && ", "}
              <Link to={`/knowledge/${m.id}`} className="text-p-link">{m.title}</Link>
            </span>
          ))}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Chip
          on={mode === "method"}
          onClick={() => {
            setMode(mode === "method" ? null : "method");
            setPrefilled(null);
            setBody(note.body_md);
          }}
        >
          Новый метод
        </Chip>
        {note.similar.length > 0 && (
          <Chip on={mode === "version"} onClick={() => setMode(mode === "version" ? null : "version")}>Версия похожего</Chip>
        )}
        <Chip on={mode === "reject"} onClick={() => setMode(mode === "reject" ? null : "reject")}>Отклонить</Chip>
      </div>
      {mode === "method" && (
        <>
          <TextField label="Название метода" value={title} maxLength={300} onChange={(e) => setTitle(e.target.value)} />
          <TextField label="Когда применять" value={summary} maxLength={240} onChange={(e) => setSummary(e.target.value)} />
        </>
      )}
      {mode === "version" && (
        <div className="flex flex-wrap gap-2">
          {note.similar.map((m) => (
            <Chip key={m.id} on={methodId === m.id} onClick={() => setMethodId(m.id)}>{m.title}</Chip>
          ))}
        </div>
      )}
      {mode === "version" && (
        <TextField label="Что добавляет версия" value={summary} maxLength={2000} onChange={(e) => setSummary(e.target.value)} />
      )}
      {(mode === "method" || mode === "version") && (
        <TextArea label="Метод по шагам" value={body} rows={8} onChange={(e) => setBody(e.target.value)} />
      )}
      {mode === "reject" && (
        <TextField label="Почему: повтор, частный случай, ошибка" value={reason} maxLength={2000} onChange={(e) => setReason(e.target.value)} />
      )}
      {mode && (
        <div>
          <Button
            variant="strong"
            disabled={!ready}
            loading={save.isPending || refuse.isPending}
            onClick={() => (mode === "reject" ? refuse.mutate() : save.mutate())}
          >
            {mode === "reject" ? "Отклонить" : "Записать"}
          </Button>
        </div>
      )}
    </section>
  );
}
